import { Group, Badge, Button, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  StainFilters,
  StainType,
} from "../../../services/admin/stains.service";

interface CptCodeOption {
  id: string;
  code: string;
  description: string;
}

interface StainActiveFiltersProps {
  filters: StainFilters;
  onFiltersChange: (filters: StainFilters) => void;
  onClearAll: () => void;
  cptCodes: CptCodeOption[];
}

export default function StainActiveFilters({
  filters,
  onFiltersChange,
  onClearAll,
  cptCodes,
}: StainActiveFiltersProps) {
  const hasAnyFilter = () => {
    return (
      (filters.stainTypes && filters.stainTypes.length > 0) ||
      filters.availability ||
      filters.status ||
      (filters.cptCodeIds && filters.cptCodeIds.length > 0) ||
      filters.showWithoutCpt
    );
  };

  if (!hasAnyFilter()) {
    return null;
  }

  const removeStainType = (type: StainType) => {
    const newTypes = filters.stainTypes?.filter((t) => t !== type);
    onFiltersChange({
      ...filters,
      stainTypes: newTypes && newTypes.length > 0 ? newTypes : undefined,
    });
  };

  const removeAvailability = () => {
    onFiltersChange({ ...filters, availability: undefined });
  };

  const removeStatus = () => {
    onFiltersChange({ ...filters, status: undefined });
  };

  const removeCptCode = (cptId: string) => {
    const newCptIds = filters.cptCodeIds?.filter((id) => id !== cptId);
    onFiltersChange({
      ...filters,
      cptCodeIds: newCptIds && newCptIds.length > 0 ? newCptIds : undefined,
    });
  };

  const removeShowWithoutCpt = () => {
    onFiltersChange({ ...filters, showWithoutCpt: undefined });
  };

  const getAvailabilityLabel = () => {
    switch (filters.availability) {
      case "inHouse":
        return "In-House";
      case "external":
        return "External";
      default:
        return "";
    }
  };

  const getStatusLabel = () => {
    switch (filters.status) {
      case "active":
        return "Active";
      case "inactive":
        return "Inactive";
      default:
        return "";
    }
  };

  const getCptCodeLabel = (cptId: string) => {
    const cpt = cptCodes.find((c) => c.id === cptId);
    return cpt ? cpt.code : cptId;
  };

  return (
    <Group gap="xs" wrap="wrap">
      <Text size="sm" c="dimmed">
        Filters:
      </Text>

      {/* Stain Type badges */}
      {filters.stainTypes?.map((type) => (
        <Badge
          key={type}
          variant="light"
          rightSection={
            <IconX
              size={12}
              style={{ cursor: "pointer" }}
              onClick={() => removeStainType(type)}
            />
          }
        >
          Type: {type}
        </Badge>
      ))}

      {/* Availability badge */}
      {filters.availability && (
        <Badge
          variant="light"
          color="blue"
          rightSection={
            <IconX
              size={12}
              style={{ cursor: "pointer" }}
              onClick={removeAvailability}
            />
          }
        >
          {getAvailabilityLabel()}
        </Badge>
      )}

      {/* Status badge */}
      {filters.status && (
        <Badge
          variant="light"
          color={filters.status === "active" ? "green" : "red"}
          rightSection={
            <IconX
              size={12}
              style={{ cursor: "pointer" }}
              onClick={removeStatus}
            />
          }
        >
          {getStatusLabel()}
        </Badge>
      )}

      {/* CPT Code badges */}
      {filters.cptCodeIds?.map((cptId) => (
        <Badge
          key={cptId}
          variant="light"
          color="violet"
          rightSection={
            <IconX
              size={12}
              style={{ cursor: "pointer" }}
              onClick={() => removeCptCode(cptId)}
            />
          }
        >
          CPT: {getCptCodeLabel(cptId)}
        </Badge>
      ))}

      {/* Without CPT badge */}
      {filters.showWithoutCpt && (
        <Badge
          variant="light"
          color="gray"
          rightSection={
            <IconX
              size={12}
              style={{ cursor: "pointer" }}
              onClick={removeShowWithoutCpt}
            />
          }
        >
          Without CPT
        </Badge>
      )}

      {/* Clear All button */}
      <Button
        variant="subtle"
        size="compact-xs"
        color="gray"
        onClick={onClearAll}
      >
        Clear All
      </Button>
    </Group>
  );
}
